import axios from 'axios';
import { CreditsService } from './credits.service';
import { autograbUsageService } from './autograb-usage.service';

const AUTOGRAB_API_KEY = import.meta.env.VITE_AUTOGRAB_API_KEY || '';
const AUTOGRAB_BASE_URL = import.meta.env.VITE_AUTOGRAB_BASE_URL || 'https://api.autograb.com.au';

console.log('🔑 Autograb API Key loaded:', AUTOGRAB_API_KEY ? `${AUTOGRAB_API_KEY.substring(0, 10)}...` : 'MISSING');
console.log('🌐 Autograb Base URL:', AUTOGRAB_BASE_URL);

if (!AUTOGRAB_API_KEY) {
  console.error('❌ AutoGrab API key is not configured. Please set VITE_AUTOGRAB_API_KEY in your environment variables.');
} else {
  console.log('✅ AutoGrab API key is configured');
}

const autograbApi = axios.create({
  baseURL: AUTOGRAB_BASE_URL,
  headers: {
    'ApiKey': AUTOGRAB_API_KEY,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// Log all requests for debugging
autograbApi.interceptors.request.use((config) => {
  console.log('🚀 Autograb API Request:', {
    url: config.url,
    method: config.method,
    baseURL: config.baseURL,
    headers: {
      ApiKey: config.headers.ApiKey ? `${config.headers.ApiKey.toString().substring(0, 10)}...` : 'MISSING',
      Accept: config.headers.Accept,
      'Content-Type': config.headers['Content-Type']
    }
  });
  return config;
}, (error) => {
  console.error('❌ Request error:', error);
  return Promise.reject(error);
});

// Service costs in credits
const SERVICE_COSTS = {
  VEHICLE_LOOKUP: 1,    // 1 credit per vehicle lookup
  VALUATION: 10,        // 10 credits per valuation
  PPSR_REPORT: 5,       // 5 credits per PPSR report
  MARKET_OVERLAY: 1,    // 1 credit per market overlay
  CAR_ANALYSIS: 3       // 3 credits per car analysis
};

export interface VehicleDetails {
  make: string;
  model: string;
  year: number;
  variant: string;
  bodyType: string;
  transmission: string;
  fuelType: string;
  engineSize?: string;
  vin?: string;
  registration?: string;
}

export interface ValuationResult {
  valuationId: string;
  lowPrice: number;
  midPrice: number;
  highPrice: number;
  confidence: number;
  marketDaysSupply?: number;
  averageKms?: number;
}

export interface PPSRResult {
  reportId: string;
  hasEncumbrance: boolean;
  stolenStatus: boolean;
  writeOffStatus: boolean;
  encumbranceDetails?: any[];
}

class AutograbService {
  // Helper function to check and deduct credits before API calls
  private async checkAndDeductCredits(
    organizationId: string,
    userId: string,
    serviceType: string,
    creditCost: number,
    description: string
  ): Promise<void> {
    if (!organizationId || !userId) {
      throw new Error('Organization ID and User ID are required for credit deduction');
    }

    // Check if organization has sufficient credits
    const hasSufficientCredits = await CreditsService.checkSufficientCredits(organizationId, creditCost);

    if (!hasSufficientCredits) {
      throw new Error(`Insufficient credits. This service requires ${creditCost} credits. Please purchase more credits to continue.`);
    }

    // Deduct credits for the service
    const success = await CreditsService.deductCreditsForService(
      organizationId,
      userId,
      creditCost,
      serviceType,
      description
    );

    if (!success) {
      throw new Error('Failed to deduct credits for service usage');
    }
  }
  // Look up vehicle by registration
  async getVehicleByRegistration(
    registration: string,
    state: string,
    organizationId?: string,
    userId?: string
  ) {
    try {
      // Check API key
      if (!AUTOGRAB_API_KEY) {
        throw new Error('AutoGrab API key is not configured. Please contact support.');
      }

      // Deduct credits if organization and user info provided
      if (organizationId && userId) {
        await this.checkAndDeductCredits(
          organizationId,
          userId,
          'autograb_vehicle_lookup',
          SERVICE_COSTS.VEHICLE_LOOKUP,
          `Vehicle lookup for registration: ${registration}`
        );
      }

      const response = await autograbApi.get(`/v2/vehicles/registrations/${registration.toUpperCase()}`, {
        params: {
          state: state.toUpperCase(),
          region: 'au'
        }
      });

      // Log API usage
      if (organizationId && userId) {
        await autograbUsageService.logUsage({
          organizationId,
          requestedBy: userId,
          serviceType: 'vin_lookup',
          creditsUsed: SERVICE_COSTS.VEHICLE_LOOKUP,
          metadata: {
            registration,
            state,
          },
        });
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching vehicle by registration:', error);

      // Handle API key errors specifically
      if (error.response?.status === 401) {
        throw new Error('AutoGrab API authentication failed. Please check API key configuration.');
      }

      throw error;
    }
  }

  // Look up vehicle by VIN
  async getVehicleByVIN(
    vin: string,
    organizationId?: string,
    userId?: string
  ) {
    try {
      // Check API key
      if (!AUTOGRAB_API_KEY) {
        throw new Error('AutoGrab API key is not configured. Please contact support.');
      }

      // Deduct credits if organization and user info provided
      if (organizationId && userId) {
        await this.checkAndDeductCredits(
          organizationId,
          userId,
          'autograb_vin_lookup',
          SERVICE_COSTS.VEHICLE_LOOKUP,
          `VIN lookup for: ${vin}`
        );
      }

      const response = await autograbApi.get(`/v2/vehicles/vins/${vin}`, {
        params: {
          region: 'au'
        }
      });

      // Log API usage
      if (organizationId && userId) {
        await autograbUsageService.logUsage({
          organizationId,
          requestedBy: userId,
          serviceType: 'vin_lookup',
          creditsUsed: SERVICE_COSTS.VEHICLE_LOOKUP,
          metadata: {
            vin,
          },
        });
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching vehicle by VIN:', error);

      // Handle API key errors specifically
      if (error.response?.status === 401) {
        throw new Error('AutoGrab API authentication failed. Please check API key configuration.');
      }

      throw error;
    }
  }

  // Get vehicle valuation - requires vehicle_id from vehicle lookup
  async getValuation(vehicleData: {
    vehicle_id?: string;
    registration?: string;
    state?: string;
    kilometres?: number;
    organizationId?: string;
    userId?: string;
  }) {
    try {
      // Check API key
      if (!AUTOGRAB_API_KEY) {
        throw new Error('AutoGrab API key is not configured. Please contact support.');
      }

      // Deduct credits for valuation
      if (vehicleData.organizationId && vehicleData.userId) {
        await this.checkAndDeductCredits(
          vehicleData.organizationId,
          vehicleData.userId,
          'autograb_valuation',
          SERVICE_COSTS.VALUATION,
          `Vehicle valuation for registration: ${vehicleData.registration || 'N/A'}`
        );
      }

      let vehicleId = vehicleData.vehicle_id;

      // If no vehicle_id provided but registration is, look it up first
      if (!vehicleId && vehicleData.registration && vehicleData.state) {
        const vehicleLookup = await this.getVehicleByRegistration(
          vehicleData.registration,
          vehicleData.state
          // Don't deduct credits again for the lookup as we already deducted for valuation
        );
        vehicleId = vehicleLookup?.vehicle?.id;
      }

      if (!vehicleId) {
        throw new Error('Vehicle ID required for valuation. Please lookup vehicle first.');
      }

      const response = await autograbApi.post('/v2/valuations/predict', {
        vehicle_id: vehicleId,
        kilometres: vehicleData.kilometres || 50000,
        state: vehicleData.state || 'NSW',
        region: 'au'
      });

      // Log API usage
      if (vehicleData.organizationId && vehicleData.userId) {
        await autograbUsageService.logUsage({
          organizationId: vehicleData.organizationId,
          requestedBy: vehicleData.userId,
          serviceType: 'valuation',
          creditsUsed: SERVICE_COSTS.VALUATION,
          metadata: {
            vehicle_id: vehicleId,
            kilometres: vehicleData.kilometres,
            registration: vehicleData.registration,
          },
        });
      }

      return response.data;
    } catch (error: any) {
      console.error('Error getting valuation:', error);

      // Handle API key errors specifically
      if (error.response?.status === 401) {
        throw new Error('AutoGrab API authentication failed. Please check API key configuration.');
      }

      // Handle insufficient credits error
      if (error.message?.includes('Insufficient credits')) {
        throw error; // Re-throw credits error as-is
      }

      throw error;
    }
  }

  // Get PPSR report
  async getPPSRReport(rego: string, state: string) {
    try {
      // Use the correct endpoint with region as query parameter
      const response = await autograbApi.post('/v2/certificates/generate', {
        rego: rego.toUpperCase(),
        state: state.toUpperCase(),
        type: 'ppsr'
      }, {
        params: {
          region: 'au'
        }
      });
      
      console.log('PPSR Report generated:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('PPSR Error Details:');
      console.error('- Status:', error.response?.status);
      console.error('- Error Data:', error.response?.data);
      
      // Return more detailed error info
      const errorInfo = {
        status: error.response?.status,
        message: error.response?.data?.message || error.response?.data?.error || error.message,
        details: error.response?.data,
        requestPayload: { rego, state, type: 'ppsr' }
      };
      
      throw errorInfo;
    }
  }

  // Get market overlay data (pricing justification)
  async getMarketOverlay(vehicleData: {
    make: string;
    model: string;
    year: number;
    variant?: string;
    bodyType?: string;
    transmission?: string;
    organizationId?: string;
    userId?: string;
  }) {
    try {
      // Deduct credits if organization and user info provided
      if (vehicleData.organizationId && vehicleData.userId) {
        await this.checkAndDeductCredits(
          vehicleData.organizationId,
          vehicleData.userId,
          'autograb_market_overlay',
          SERVICE_COSTS.MARKET_OVERLAY,
          `Market overlay for ${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`
        );
      }

      const response = await autograbApi.post('/v2/sourcing/overlay', {
        make: vehicleData.make,
        model: vehicleData.model,
        year: vehicleData.year,
        variant: vehicleData.variant,
        bodyType: vehicleData.bodyType,
        transmission: vehicleData.transmission,
        region: 'au',
        features: ['avg_price', 'price_range', 'days_supply', 'avg_kms']
      });

      // Log API usage
      if (vehicleData.organizationId && vehicleData.userId) {
        await autograbUsageService.logUsage({
          organizationId: vehicleData.organizationId,
          requestedBy: vehicleData.userId,
          serviceType: 'market_overlay',
          creditsUsed: SERVICE_COSTS.MARKET_OVERLAY,
          metadata: {
            make: vehicleData.make,
            model: vehicleData.model,
            year: vehicleData.year,
            variant: vehicleData.variant,
          },
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error getting market overlay:', error);
      throw error;
    }
  }

  // Get Car Analysis Report
  async getCarAnalysisReport(
    vin: string,
    organizationId?: string,
    userId?: string
  ) {
    try {
      // Deduct credits if organization and user info provided
      if (organizationId && userId) {
        await this.checkAndDeductCredits(
          organizationId,
          userId,
          'autograb_car_analysis',
          SERVICE_COSTS.CAR_ANALYSIS,
          `Car analysis for VIN: ${vin}`
        );
      }

      const response = await autograbApi.post('/v2/reports/car_analysis', {
        vin: vin,
        region: 'au'
      });

      // Log API usage
      if (organizationId && userId) {
        await autograbUsageService.logUsage({
          organizationId,
          requestedBy: userId,
          serviceType: 'car_analysis',
          creditsUsed: SERVICE_COSTS.CAR_ANALYSIS,
          metadata: {
            vin,
          },
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error getting car analysis report:', error);
      throw error;
    }
  }

  // Test connection with simple endpoint
  async testConnection() {
    console.log('Testing Autograb API connection...');
    
    try {
      // Try a simple test with a known good registration
      const response = await autograbApi.get('/v2/vehicles/registrations/ABC123', {
        params: { state: 'NSW', region: 'au' }
      });
      console.log('✓ API Connection successful');
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error('✗ API Connection failed:', error.response?.data || error.message);
      return { 
        success: false, 
        error: error.response?.data || error.message,
        status: error.response?.status,
        headers: error.response?.headers
      };
    }
  }

  // Test PPSR endpoint specifically
  async testPPSREndpoint() {
    try {
      // Test with a minimal request - region as query parameter
      const response = await autograbApi.post('/v2/certificates/generate', {
        rego: 'TEST123',
        state: 'NSW',
        type: 'ppsr'
      }, {
        params: {
          region: 'au'
        }
      });
      return { success: true, data: response.data };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data,
        status: error.response?.status,
        request: { rego: 'TEST123', state: 'NSW', type: 'ppsr' },
        queryParams: { region: 'au' }
      };
    }
  }

  /**
   * Get Factory Build Data (Fitted Options)
   * Returns the actual options/equipment fitted to this specific vehicle at the factory
   *
   * @param vin - Vehicle Identification Number
   * @param organizationId - Optional organization ID for credit tracking
   * @param userId - Optional user ID for credit tracking
   * @returns Factory build data including features array with code/description
   */
  async getFactoryBuildData(
    vin: string,
    organizationId?: string,
    userId?: string
  ) {
    try {
      // Check API key
      if (!AUTOGRAB_API_KEY) {
        throw new Error('AutoGrab API key is not configured. Please contact support.');
      }

      // Deduct credits if organization and user info provided
      if (organizationId && userId) {
        await this.checkAndDeductCredits(
          organizationId,
          userId,
          'autograb_factory_build',
          SERVICE_COSTS.VEHICLE_LOOKUP, // 1 credit
          `Factory build data for VIN: ${vin}`
        );
      }

      const response = await autograbApi.get(`/v2/build-data/${vin}`, {
        params: {
          region: 'au'
        }
      });

      // Log API usage
      if (organizationId && userId) {
        await autograbUsageService.logUsage({
          organizationId,
          requestedBy: userId,
          serviceType: 'factory_build_data',
          creditsUsed: SERVICE_COSTS.VEHICLE_LOOKUP,
          metadata: {
            vin,
          },
        });
      }

      return response.data;
    } catch (error: any) {
      console.error('Error getting factory build data:', error);

      // Handle API key errors specifically
      if (error.response?.status === 401) {
        throw new Error('AutoGrab API authentication failed. Please check API key configuration.');
      }

      throw error;
    }
  }
}

export default new AutograbService();